import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { useAuth, defaultRoute } from "../context/AuthContext";
import { useLang } from "../lib/i18n";
import { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const doLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      navigate(user.must_change_password ? "/change-password" : defaultRoute(user.role));
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex w-[42%] bg-[#1E3A8A] text-white flex-col justify-between p-12">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-white/10 border border-white/20 flex items-center justify-center font-heading font-bold text-lg">
            A
          </div>
          <div>
            <div className="font-heading font-semibold text-xl">APHA</div>
            <div className="text-xs text-blue-200">{t("appTagline")}</div>
          </div>
        </div>
        <div>
          <h1 className="font-heading text-4xl font-semibold leading-tight max-w-md">
            Daily updates in. Delivery risks out.
          </h1>
          <p className="mt-4 text-blue-200 max-w-md text-base leading-relaxed">
            Team members report in under two minutes. APHA correlates updates with Jira
            evidence, detects risks, and recommends the next PM action — every working day.
          </p>
        </div>
        <div className="flex items-center gap-2 text-blue-200 text-sm">
          <ShieldCheck className="h-4 w-4" />
          Advisory only — never an employee scoring system.
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-6 flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-[#1E3A8A] text-white flex items-center justify-center font-heading font-bold">A</div>
            <span className="font-heading font-semibold text-lg">APHA</span>
          </div>
          <h2 className="font-heading text-2xl font-semibold">{t("signIn")}</h2>
          <form className="mt-6 space-y-4" onSubmit={doLogin}>
            <div className="space-y-1.5">
              <Label htmlFor="email">{t("email")}</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">{t("password")}</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {error && (
              <div data-testid="login-error" className="text-sm text-[#991B1B] bg-[#FEE2E2] border border-[#FECACA] rounded-md px-3 py-2">
                {error}
              </div>
            )}
            <Button data-testid="login-submit-button" type="submit" className="w-full bg-[#1E3A8A] hover:bg-[#1c3479]" disabled={loading}>
              {loading ? "..." : t("signIn")}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
